"use client";

import Image from "next/image";
import hero from "@/public/hero/hero.webp";
import heroMobile from "@/public/hero/hero-mobile.webp";
import shapesBackground from "@/public/shapes-background.svg";
import { motion } from "motion/react";
import { HeroBottomBar } from "./hero-bottom-bar";
import { HeroVideo } from "./hero-video";
import { Title } from "./ui/title";
import { EASE } from "@/lib/motion";

type Props = {
  desktopVideoUrl: string | null;
  mobileVideoUrl: string | null;
};

export function HeroContent({ desktopVideoUrl, mobileVideoUrl }: Props) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 0 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.65, ease: EASE }}
    >
      <div
        data-header-theme="dark"
        className="h-svh min-h-svh w-full relative overflow-hidden"
      >
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-t from-brand-900/80 to-transparent z-10" />

        <motion.div
          className="flex flex-col gap-12 items-center justify-center h-full mx-auto relative z-10"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <motion.div
            className="flex flex-col items-center justify-center p-4 gap-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4, ease: EASE }}
            >
              <Title
                variant="hero"
                tone="inverse"
                align="center"
                accent="perfection"
              >
                Details make
              </Title>
            </motion.div>
            <motion.p
              className="text-background lg:text-lg font-light tracking-[-0.02em] leading-[1.5em] text-center max-w-[585px]"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6, ease: EASE }}
            >
              Stručnost, iskustvo i pedantnost — u ugodnom okruženju koje
              odražava našu posvećenost kvaliteti, preciznosti i individualnom
              pristupu svakom pacijentu.
            </motion.p>
          </motion.div>
          <motion.div>
            <HeroBottomBar />
          </motion.div>
        </motion.div>

        <div className="absolute top-0 left-0 h-full w-full overflow-hidden">
          <div className="flex size-full relative">
            <div className="relative h-full w-full flex-shrink-0 flex-grow-0">
              {desktopVideoUrl && mobileVideoUrl ? (
                <HeroVideo
                  desktopVideoUrl={desktopVideoUrl}
                  mobileVideoUrl={mobileVideoUrl}
                />
              ) : (
                <>
                  <Image
                    src={hero}
                    alt="Hero desktop"
                    fill
                    className="object-cover object-center hidden md:block"
                    sizes="92vw"
                    priority
                    placeholder="blur"
                  />

                  <Image
                    src={heroMobile}
                    alt="Hero mobile"
                    fill
                    className="object-cover object-center block md:hidden"
                    sizes="92vw"
                    priority
                    placeholder="blur"
                  />
                </>
              )}
            </div>
            <div className="absolute size-full bg-gradient-to-b from-brand-800/80 to-brand-800/30" />
            <Image
              src={shapesBackground}
              alt="Shapes background"
              fill
              className="pointer-events-none absolute inset-0 size-full object-cover opacity-5"
              sizes="100vw"
              priority
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
}
